import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { uploadSkuPhoto, deleteSkuPhoto, reorderSkuPhotos } from '../api';
import { wmsSkuKeys } from './useWmsSkus';
import type { Sku } from '../types';

function getErrorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  return 'An unexpected error occurred';
}

export function useUploadSkuPhoto() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ skuId, file }: { skuId: string; file: File }) =>
      uploadSkuPhoto(skuId, file),
    onSuccess: () => {
      // Invalidate all SKU queries to refresh list thumbnails and detail photos
      queryClient.invalidateQueries({ queryKey: wmsSkuKeys.all });
    },
    // No onError toast — SkuPhotoManager handles per-file errors via Promise.allSettled
  });
}

export function useDeleteSkuPhoto() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ skuId, photoId }: { skuId: string; photoId: string }) =>
      deleteSkuPhoto(skuId, photoId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: wmsSkuKeys.all });
      toast.success('Photo deleted');
    },
    onError: (error) => {
      toast.error('Failed to delete photo', { description: getErrorMessage(error) });
    },
  });
}

export function useReorderSkuPhotos() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ skuId, photoIds }: { skuId: string; photoIds: string[] }) =>
      reorderSkuPhotos(skuId, photoIds),
    onMutate: async ({ skuId, photoIds }) => {
      // Cancel any outgoing refetches for this SKU detail
      await queryClient.cancelQueries({ queryKey: wmsSkuKeys.detail(skuId) });

      // Snapshot current data for rollback
      const previous = queryClient.getQueryData<Sku>(wmsSkuKeys.detail(skuId));

      // Optimistically reorder photos in the cache
      if (previous?.photos) {
        const idToIndex = new Map(photoIds.map((id, i) => [id, i]));
        const reordered = [...previous.photos].sort(
          (a, b) => (idToIndex.get(a.id) ?? 0) - (idToIndex.get(b.id) ?? 0),
        );
        queryClient.setQueryData<Sku>(wmsSkuKeys.detail(skuId), {
          ...previous,
          photos: reordered,
        });
      }

      return { previous, skuId };
    },
    onError: (error, _vars, context) => {
      // Roll back optimistic update on failure
      if (context?.previous) {
        queryClient.setQueryData(wmsSkuKeys.detail(context.skuId), context.previous);
      }
      toast.error('Failed to reorder photos', { description: getErrorMessage(error) });
    },
    onSettled: (_data, _error, { skuId }) => {
      queryClient.invalidateQueries({ queryKey: wmsSkuKeys.detail(skuId) });
    },
  });
}
